import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Memory as MemoryIcon,
  Notifications as NotificationIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, query, where, getDocs, deleteDoc, doc, orderBy } from 'firebase/firestore'
import toast from 'react-hot-toast'

const Memory = ({ userData, onBack, onNavigateToTranslate }) => {
  const { user } = useAuth()
  const [memoryItems, setMemoryItems] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (user && user.uid) {
      loadMemoryItems()
    }
  }, [user])

  const loadMemoryItems = async () => {
    if (!user || !user.uid) {
      console.log('User not ready yet')
      return
    }

    try {
      setLoading(true)
      console.log('Loading memory items for user:', user.uid)
      
      // Android uygulamasındaki aynı yapıyı kullan
      const memoryRef = collection(db, 'users', user.uid, 'memory')
      const q = query(memoryRef, orderBy('timestamp', 'desc'))
      const querySnapshot = await getDocs(q)
      
      console.log('Found memory items:', querySnapshot.docs.length)
      
      const items = querySnapshot.docs.map(doc => {
        const data = doc.data()
        console.log('Memory item:', data)
        return {
          id: doc.id,
          ...data
        }
      })
      
      console.log('Memory items loaded:', items)
      setMemoryItems(items)
    } catch (error) {
      console.error('Error loading memory items:', error)
      toast.error('Hafıza öğeleri yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteItem = async (itemId) => {
    try {
      // Android uygulamasındaki aynı yapıyı kullan
      await deleteDoc(doc(db, 'users', user.uid, 'memory', itemId))
      setMemoryItems(prev => prev.filter(item => item.id !== itemId))
      toast.success('Öğe silindi')
    } catch (error) {
      console.error('Error deleting memory item:', error)
      toast.error('Öğe silinirken hata oluştu')
    }
  }

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Panoya kopyalandı!')
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return ''
    
    // Android timestamp'i Date objesine çevir
    const dateObj = new Date(timestamp)
    return dateObj.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getLanguageLabel = (lang) => {
    return lang === 'tr' ? 'Türkçe' : lang === 'en' ? 'İngilizce' : lang
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Hafıza
          </Typography>
          <IconButton color="inherit" onClick={() => toast.success('Bildirimler açılıyor')}>
            <NotificationIcon />
          </IconButton>
          <IconButton 
            color="inherit" 
            onClick={loadMemoryItems}
            disabled={!user || !user.uid}
          >
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {!user || !user.uid ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Kullanıcı bilgileri yükleniyor...</Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
                 ) : memoryItems.length === 0 ? (
           <Card sx={{ 
             borderRadius: 3,
             boxShadow: 3,
             background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
           }}>
             <CardContent sx={{ textAlign: 'center', py: 6 }}>
               <MemoryIcon sx={{ fontSize: 80, color: 'primary.main', mb: 3 }} />
               <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                 Hafızanız Boş
               </Typography>
               <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                 Henüz hiçbir çeviri hafızaya kaydedilmemiş.
               </Typography>
                             <Button
                 variant="contained"
                 size="large"
                 fullWidth
                 sx={{ 
                   mt: 3,
                   py: 2,
                   fontSize: '1.2rem',
                   fontWeight: 'bold',
                   background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                   boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                   '&:hover': {
                     background: 'linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)',
                     transform: 'translateY(-2px)',
                     boxShadow: '0 5px 8px 2px rgba(33, 203, 243, .4)'
                   }
                 }}
                 onClick={() => {
                   // Çeviri sayfasına yönlendir
                   if (onNavigateToTranslate) {
                     onNavigateToTranslate()
                   } else if (onBack) {
                     onBack()
                   }
                 }}
               >
                 🚀 Çeviri Yap
               </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography variant="h5" fontWeight="bold" color="primary">
                Kaydedilen Çeviriler ({memoryItems.length})
              </Typography>
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  if (window.confirm('Tüm hafızayı temizlemek istediğinizden emin misiniz?')) {
                    // Tüm öğeleri sil
                    memoryItems.forEach(item => handleDeleteItem(item.id))
                  }
                }}
              >
                Tümünü Temizle
              </Button>
            </Box>

            {/* Memory Items List */}
            <List>
              {memoryItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  <Card sx={{ mb: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      {/* Header */}
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={`${getLanguageLabel(item.fromLanguage)} → ${getLanguageLabel(item.toLanguage)}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(item.timestamp)}
                          </Typography>
                        </Box>
                        <MuiIconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          <DeleteIcon />
                        </MuiIconButton>
                      </Box>

                      {/* Original Text */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Orijinal Metin:
                        </Typography>
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            {item.originalText}
                          </Typography>
                          <MuiIconButton
                            size="small"
                            onClick={() => handleCopyToClipboard(item.originalText)}
                          >
                            <CopyIcon fontSize="small" />
                          </MuiIconButton>
                        </Box>
                      </Box>

                      {/* Translated Text */}
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Çeviri:
                        </Typography>
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: 'primary.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'primary.200',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 'medium' }}>
                            {item.translatedText}
                          </Typography>
                          <MuiIconButton
                            size="small"
                            color="primary"
                            onClick={() => handleCopyToClipboard(item.translatedText)}
                          >
                            <CopyIcon fontSize="small" />
                          </MuiIconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                  {index < memoryItems.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            {/* Info Alert */}
            <Alert severity="info" sx={{ mt: 2 }}>
              Çevirilerinizi hafızaya kaydedebilir ve daha sonra buradan görüntüleyebilirsiniz.
            </Alert>
          </>
        )}
      </Container>
      </Box>
    </SpaceTheme>
  )
}

export default Memory
